// Session history state for AskPsdPageV2 — lists past conversations, resumes
// one into the turns list, deletes, and tracks the currently active session
// so new questions append to it instead of starting fresh each time.
import { useState, useCallback, useEffect } from 'react';
import { normalizeApiResponse } from '../services/responseNormalizer';
import { logger } from '../services/logger';

const mapHistoryTurn = (turn, index) => ({
  id: turn.turnId || `session-turn-${index}`,
  question: turn.question,
  steps: [],
  response:
    turn.status === 'SUCCESS' && turn.result
      ? normalizeApiResponse(turn.result)
      : undefined,
  error:
    turn.status !== 'SUCCESS'
      ? turn.errorMessage || 'This question failed.'
      : undefined,
});

export function useSessionHistory(api) {
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState(null);

  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      const data = await api.listSessions();
      // Backend returns a Spring Data Page: { content: [...], totalElements, ... }
      // — also tolerate a plain array or { sessions: [...] } in case that shape changes.
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.content)
          ? data.content
          : Array.isArray(data?.sessions)
            ? data.sessions
            : [];
      setSessions(list);
    } catch (err) {
      logger.error('Failed to load sessions', err);
      setSessionsError('Failed to load sessions.');
    } finally {
      setSessionsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const resumeSession = useCallback(
    async (sessionId) => {
      const turnsData = await api.getSessionTurns(sessionId);
      const turnsList = Array.isArray(turnsData) ? turnsData : [];
      setActiveSessionId(sessionId);
      return turnsList.map(mapHistoryTurn);
    },
    [api]
  );

  const deleteSession = useCallback(
    async (sessionId) => {
      try {
        await api.deleteSession(sessionId);
        setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
        setActiveSessionId((prev) => (prev === sessionId ? null : prev));
      } catch (err) {
        logger.error('Failed to delete session', err);
      }
    },
    [api]
  );

  const startNewSession = useCallback(() => {
    setActiveSessionId(null);
  }, []);

  // Called once a live ask() returns a sessionId — either a brand-new one
  // (first message of a fresh conversation) or the one already active.
  const registerSession = useCallback((sessionId, question) => {
    if (!sessionId) {
      return;
    }
    setActiveSessionId(sessionId);
    setSessions((prev) => {
      const now = new Date().toISOString();
      if (prev.some((s) => s.sessionId === sessionId)) {
        return prev.map((s) =>
          s.sessionId === sessionId
            ? {
                ...s,
                lastQuestion: question,
                turnCount: (s.turnCount || 0) + 1,
                updatedAt: now,
              }
            : s
        );
      }
      return [
        {
          sessionId,
          title: question,
          lastQuestion: question,
          turnCount: 1,
          updatedAt: now,
        },
        ...prev,
      ];
    });
  }, []);

  return {
    sessions,
    activeSessionId,
    sessionsLoading,
    sessionsError,
    resumeSession,
    deleteSession,
    startNewSession,
    registerSession,
  };
}
